import { useEffect, useState } from 'react';

import { Link, useNavigate, useParams } from 'react-router-dom';

import { OverviewCreateChronologyModal } from './components/dialogs/overview-create-chronology';
import { KeyDocumentsSection } from './components/documents-overview';
import { KeyEntitiesSection } from './components/entities-overview';
import { FactsOverview } from './components/facts-overview';
import { ThemesOverview } from './components/issues-overview';
import { KimOverview } from './components/kim-overview';
import { Summary } from './components/summary';
import { SummaryLoader } from './components/summary-loader';
import { PageName, TrackingEvents } from '@/analytics/events';
import { trackCustomEvent, trackPageView } from '@/analytics/Mixpanel';
import { useGetCase } from '@/api/queries/useGetCase';
import useGetCaseOverview from '@/api/queries/useGetCaseOverview';
import { useGetInconsistencies } from '@/api/queries/useGetInconsistencies';
import useGetSummary from '@/api/queries/useGetSummary';
import { Breadcrumb } from '@/new/components/breadcrumb';
import { Icon } from '@/new/components/icon';
import { PageContainer } from '@/new/components/page-container';
import { PageHeader } from '@/new/components/page-header';
import { PageTitle } from '@/new/components/page-title';
import { Button } from '@/new/components/shadcn/button';
import { KimMode } from '@/types';

function SummaryHeading({
  title,
  description,
  icon,
  children,
}: {
  title: string;
  description: string;
  icon: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-teal-50 to-teal-100">
          <Icon className={`${icon} h-5 w-5 text-teal-600`} />
        </div>
        <div>
          <h2 className="font-serif text-lg font-medium text-gray-900">{title}</h2>
          <p className="text-sm text-gray-500">{description}</p>
        </div>
      </div>
      {children}
    </div>
  );
}

export function SubsetOverview() {
  const { matterId, subsetId } = useParams();
  if (!matterId || !subsetId) return;

  const navigate = useNavigate();

  const [addToChronologyOpen, setAddToChronologyOpen] = useState(false);

  const { data: summaries, isLoading: summaryIsLoading } = useGetSummary(matterId);
  const { data: overview, isLoading: overviewIsLoading } = useGetCaseOverview(matterId);
  const { data: caseData } = useGetCase(matterId);
  const { data: inconsistenciesData } = useGetInconsistencies({
    caseId: matterId,
    page: 1,
    pageSize: 1,
    filters: {},
  });

  useEffect(() => {
    trackPageView(PageName.CaseSummary);
  }, []);

  const handleAskQuestion = (question: string, mode: KimMode) => {
    if (!matterId || !question.trim()) return;
    navigate(`/matters/${matterId}/subsets/${subsetId}/kim`, { state: { question, mode } });
  };

  const isDeletedDocRunOngoing =
    overview?.runs?.[0] && overview.runs[0].completedAt === null && overview.runs[0].type === 'deleteDocuments';

  return (
    <>
      <Breadcrumb
        items={
          caseData
            ? [
                { href: '/matters', label: 'Matters', active: false },
                {
                  href: `/matters/${caseData.id}`,
                  label: caseData.name,
                  active: true,
                },
                {
                  href: `/matters/${caseData.id}/subsets`,
                  label: '<subset name>',
                  active: false,
                },
              ]
            : [{ href: '/matters', label: 'Matters', active: false }]
        }
      />

      <PageContainer>
        <PageHeader>
          <PageTitle text={caseData?.name ?? ''} iconClass="lucide--group" />
        </PageHeader>
        {summaryIsLoading ? (
          <SummaryLoader />
        ) : (
          <div className="space-y-6">
            <SummaryHeading
              title="Subset Summary"
              description="Summary of this document subset."
              icon="lucide--file-text"
            >
              <div className="flex gap-2">
                <Button asChild variant="primary" className="flex items-center gap-2">
                  <Link
                    to={`/matters/${matterId}/settings/documents`}
                    onClick={() => trackCustomEvent(TrackingEvents.AddDocumentsOnSummary)}
                  >
                    <Icon className="lucide--file-plus h-4 w-4" />
                    Add Documents To Subset
                  </Link>
                </Button>
                <Button asChild className="flex items-center gap-2">
                  <Link
                    to={`/matters/${matterId}/settings`}
                    onClick={() => trackCustomEvent(TrackingEvents.ChangeIssuesOnSummary)}
                  >
                    <Icon className="lucide--pen-line h-4 w-4" />
                    Edit Subset Issues
                  </Link>
                </Button>
              </div>
            </SummaryHeading>
            <Summary
              summaryText={summaries?.content?.[0]?.content ?? ''}
              isDeletedDocRunOngoing={isDeletedDocRunOngoing ?? false}
              isLoading={summaryIsLoading}
            />

            <hr />

            <SummaryHeading title="Kim" description="Ask Kim a question about this subset." icon="lucide--bot" />
            <KimOverview caseObject={caseData} onAskQuestion={handleAskQuestion} />

            <hr />

            <SummaryHeading
              title="Subset Overview"
              description="Key information about this document subset."
              icon="lucide--square-chart-gantt"
            />

            <div className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-9 lg:items-stretch">
              <FactsOverview
                isLoading={overviewIsLoading}
                totalInconsistencies={inconsistenciesData?.totalCount ?? 0}
                overview={overview}
                onCreateChronology={() => setAddToChronologyOpen(true)}
              />
              <KeyDocumentsSection overview={overview} overviewIsLoading={overviewIsLoading} />
              <KeyEntitiesSection caseId={matterId} />
            </div>

            <div className="mb-6">
              <ThemesOverview themes={summaries?.content?.slice(1) ?? []} isLoading={summaryIsLoading} />
            </div>
          </div>
        )}

        <OverviewCreateChronologyModal
          isOpen={addToChronologyOpen}
          onOpenChange={setAddToChronologyOpen}
          caseId={matterId}
          totalKeyFacts={overview?.keyFacts ?? 0}
          totalRelevantFacts={overview?.relevantFacts ?? 0}
        />
      </PageContainer>
    </>
  );
}
